import { formatGaslessRelayError } from "./gaslessRelayError";
import {
  parseRelayerResponse,
  parseRelayerResponseSoft,
} from "./relayerResponse";
import { MutableRefObject } from "react";
import { ethers } from "ethers";
import { getWeb3ProviderForChain } from "../../../utils/wallet/activeSigner";
import { getCurrentChainConfig } from "../../../config/chains";
import { getSpotDomainName } from "../../../utils/gaslessDomains";
import { InviteStatusResponse } from "../invite/useInviteStatus";
import { ICancelSpotOrderParams } from "./useSpotOrder.types";
import {
  resolveSpotSigner,
  CANCEL_SPOT_ORDER_TYPES,
  CLOB_ENGINE_ORDER_ABI,
} from "./useSpotOrder.helpers";

export interface ICancelSpotOrderCtx {
  account?: string | null;
  inviteStatus: InviteStatusResponse | undefined;
  walletTransactionInFlightRef: MutableRefObject<boolean>;
  setIsLoading: (value: boolean) => void;
  revalidateOrdersSoon: () => void;
}

export interface ICancelSpotOrdersBatchCtx {
  account?: string | null;
  revalidateOrdersSoon: () => void;
}

export async function cancelSpotOrderImpl(
  params: ICancelSpotOrderParams,
  ctx: ICancelSpotOrderCtx
) {
  const {
    account,
    inviteStatus,
    walletTransactionInFlightRef,
    setIsLoading,
    revalidateOrdersSoon,
  } = ctx;
  if (!account) throw new Error("Not connected");
  if (!inviteStatus) {
    throw new Error("Checking invite access. Please try again in a moment.");
  }
  if (inviteStatus?.invite_required && !inviteStatus.allowed) {
    throw new Error("Invite code required to trade");
  }
  if (walletTransactionInFlightRef.current) {
    throw new Error(
      "Transaction already in progress. Please confirm or reject the open wallet request."
    );
  }

  const config = getCurrentChainConfig();
  const routerAddress = config.contracts.perpetualDex?.router;
  if (!routerAddress) throw new Error("Router address not configured");

  walletTransactionInFlightRef.current = true;
  setIsLoading(true);
  try {
    const provider = await getWeb3ProviderForChain(config);
    const signer = provider.getSigner();

    // ── Gasless spot cancel: sign EIP-712 CancelSpotOrder + relay (user pays NO gas) ──
    // One-click signs with the agent key (no popup); otherwise the connected wallet signs once.
    if (config.gasless && config.relayerUrl) {
      const relayer = config.relayerUrl.replace(/\/+$/, "");
      const nonceRes = await fetch(`${relayer}/nonce/${account}?router=spot`);
      const nonce: string = String((await parseRelayerResponse(nonceRes, "nonce")).nonce);
      const deadline = String(Math.floor(Date.now() / 1000) + 3600);
      const spotDomainName = getSpotDomainName(config);
      const domain = {
        name: spotDomainName,
        version: "1",
        chainId: config.chainId,
        verifyingContract: routerAddress,
      };
      const message = {
        user: account,
        orderBook: params.instrument_id,
        orderId: ethers.BigNumber.from(params.order_id),
        nonce,
        deadline,
      };
      const signWith: any = await resolveSpotSigner(
        account, config.chainId, routerAddress, spotDomainName, config.relayerUrl, provider, signer
      );
      const signature = await signWith._signTypedData(domain, CANCEL_SPOT_ORDER_TYPES, message);
      const relayRes = await fetch(`${relayer}/relay`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          action: "cancelSpotOrder",
          params: {
            user: account,
            baseCurrency: params.base_currency,
            quoteCurrency: params.quote_currency,
            orderBook: params.instrument_id,
            orderId: Number(params.order_id),
            nonce,
            deadline,
          },
          signature,
        }),
      });
      const relayJson: any = await parseRelayerResponseSoft(relayRes, "order relay");
      if (!relayRes.ok || relayJson?.success === false || !relayJson?.txHash) {
        throw new Error(
          formatGaslessRelayError(relayJson?.error, "Gasless cancel failed. Please try again.", {
            reverted: relayJson?.reverted,
            action: "cancel",
          })
        );
      }
      revalidateOrdersSoon();
      return { order_id: params.order_id, tx_hash: relayJson.txHash };
    }

    const engine = new ethers.Contract(
      routerAddress,
      CLOB_ENGINE_ORDER_ABI,
      signer
    );

    const pool = {
      baseCurrency: params.base_currency,
      quoteCurrency: params.quote_currency,
      orderBook: params.instrument_id,
    };
    const callArgs = [
      [pool.baseCurrency, pool.quoteCurrency, pool.orderBook],
      ethers.BigNumber.from(params.order_id),
    ] as const;
    const estimatedGas = await engine.estimateGas.cancelOrder(...callArgs);
    const gasLimit = estimatedGas.mul(120).div(100);
    const tx = await engine.cancelOrder(...callArgs, { gasLimit });
    await tx.wait();
    revalidateOrdersSoon();

    return {
      order_id: params.order_id,
      tx_hash: tx.hash,
    };
  } finally {
    walletTransactionInFlightRef.current = false;
    setIsLoading(false);
  }
}

// Gasless "cancel all" for spot: group the given open spot orders by pool and, per pool, sign one
// CancelBatchOrders and relay it (on-chain cancelOrderBatchBySignature). One-click signs with the
// agent key; otherwise the wallet signs once per pool. Only touches the orders passed in — so it
// is asset-scoped and never wipes unrelated markets (unlike the account-wide backend cancel-all).
export async function cancelSpotOrdersBatchImpl(
  spotOrders: Array<{
    order_id?: string;
    instrument_id?: string;
    base_currency?: string;
    quote_currency?: string;
  }>,
  ctx: ICancelSpotOrdersBatchCtx
): Promise<{ cancelled: string[]; failed: string[]; failed_details: any[] }> {
  const { account, revalidateOrdersSoon } = ctx;
  if (!account) throw new Error("Not connected");
  const config = getCurrentChainConfig();
  const routerAddress = config.contracts.perpetualDex?.router;
  if (!routerAddress) throw new Error("Router address not configured");
  if (!config.gasless || !config.relayerUrl) {
    throw new Error("Gasless relayer not available for spot batch cancel");
  }
  const relayer = config.relayerUrl.replace(/\/+$/, "");
  const provider = await getWeb3ProviderForChain(config);
  const signer = provider.getSigner();
  const spotDomainName = getSpotDomainName(config);
  const signWith: any = await resolveSpotSigner(
    account, config.chainId, routerAddress, spotDomainName, config.relayerUrl, provider, signer
  );

  // Group valid orders by pool (orderBook).
  const byPool = new Map<string, { base: string; quote: string; ids: number[] }>();
  for (const o of spotOrders) {
    if (!o.order_id || !/^\d+$/.test(o.order_id)) continue;
    if (!o.instrument_id || !o.base_currency || !o.quote_currency) continue;
    const book = o.instrument_id;
    if (!byPool.has(book)) byPool.set(book, { base: o.base_currency, quote: o.quote_currency, ids: [] });
    byPool.get(book)!.ids.push(Number(o.order_id));
  }

  const cancelled: string[] = [];
  const failedDetails: any[] = [];
  const domain = {
    name: spotDomainName,
    version: "1",
    chainId: config.chainId,
    verifyingContract: routerAddress,
  };
  const types = {
    CancelBatchOrders: [
      { name: "user", type: "address" },
      { name: "orderBook", type: "address" },
      { name: "orderIdsHash", type: "bytes32" },
      { name: "nonce", type: "uint256" },
      { name: "deadline", type: "uint256" },
    ],
  };

  // Each pool consumes one spot nonce; submit sequentially and re-fetch the nonce per pool.
  for (const [book, { base, quote, ids }] of byPool) {
    if (ids.length === 0) continue;
    try {
      const nonceRes = await fetch(`${relayer}/nonce/${account}?router=spot`);
      const nonce: string = String((await parseRelayerResponse(nonceRes, "nonce")).nonce);
      const deadline = String(Math.floor(Date.now() / 1000) + 3600);
      const orderIdsHash = ethers.utils.solidityKeccak256(["uint48[]"], [ids]);
      const message = { user: account, orderBook: book, orderIdsHash, nonce, deadline };
      const signature = await signWith._signTypedData(domain, types, message);
      const relayRes = await fetch(`${relayer}/relay`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          action: "cancelBatchOrders",
          params: {
            user: account, baseCurrency: base, quoteCurrency: quote, orderBook: book,
            orderIds: ids, nonce, deadline,
          },
          signature,
        }),
      });
      const relayJson: any = await parseRelayerResponseSoft(relayRes, "order relay");
      if (!relayRes.ok || relayJson?.success === false || !relayJson?.txHash) {
        throw new Error(
          formatGaslessRelayError(relayJson?.error, "Gasless cancel failed.", {
            reverted: relayJson?.reverted,
            action: "cancel",
          })
        );
      }
      ids.forEach((id) => cancelled.push(String(id)));
    } catch (e: any) {
      ids.forEach((id) =>
        failedDetails.push({ order_id: String(id), reason: e?.message || "Cancel failed", instrument_name: book })
      );
    }
  }
  revalidateOrdersSoon();
  return { cancelled, failed: failedDetails.map((d) => String(d.order_id)), failed_details: failedDetails };
}
